import React, { useRef } from "react";
import { Alert, Box, Button, Card, CheckboxField, DateField, MenuItem, RadioField, SelectField, Stack, TextField, Typography } from "@curves/ui-base";
import { displayDateText, isValidDate, parseDate } from "@util/moment";
import { convertBooleanToOptionArray, getCheckedBooleanValue, getErrorModuleStatus, isEqual, refIsRequiredError } from "@util/utils";
import { Link, useHistory } from "react-router-dom";
import useShowPDF from "@util/hook/useShowPDF";
import useMessageDialog from "@util/hook/useMessageDialog";
import { CLUB_DOCUMENTS_ROUTER } from "@icoach/router/routerPath";
import useMembershipQuitForm from "@icoach/documents/membership/quit/hook/useMembershipQuitForm";
import { handleInitialFormData } from "@icoach/documents/membership/quit/staticData";
import { FieldGroup } from "@icoach/documents/components/Layout";
import { Grid } from "@mui/material";
import { EmployeeSelect, ReadTextField, SignBox } from "@icoach/components";
import MembershipQuitTrialCalculation101 from "@icoach/documents/membership/quit/versionBefore104/MembershipQuitTrialCalculation101";
import MembershipQuitTrialCalculation102 from "@icoach/documents/membership/quit/versionBefore104/MembershipQuitTrialCalculation102";
import MembershipQuitSettlement101 from "@icoach/documents/membership/quit/versionBefore104/MembershipQuitSettlement101";
import MembershipQuitSettlement102 from "@icoach/documents/membership/quit/versionBefore104/MembershipQuitSettlement102";
import { MembershipQuitFormProvider } from "@icoach/documents/membership/quit/MembershipQuitFormContext";

const latestVersions = [102, 103];

const MembershipQuitForm = ({ sourceData, peopleType, referenceID, isReadOnly, dialogRef, focusModeRef }) => {
	const { resource, membership, hasDeviceOrder, orderType, ...other } = sourceData || {};
	const history = useHistory();
	const showPDF = useShowPDF();
	const setMessageDialog = useMessageDialog();
	const formRef = useRef({});
	const trialCalculationRef = useRef(null); // 試用計算
	const settlementRef = useRef(null); // 結算計算
	const rentCabinetConfirmRef = useRef(null); // 置物櫃確認，後端不記
	const deviceConfirmRef = useRef(null); // 智能系統確認，後端不記
	const returnHistoryPath = CLUB_DOCUMENTS_ROUTER.QUIT.GET_HISTORY_PATH(peopleType, referenceID);

	const navigateToCompletedContract = ({ pdfPath, targetPath }) => {
		showPDF({ open: true, pdfUrl: pdfPath });
		history.push(targetPath);
	};

	const onSubmitFormSuccess = ({ completedPath, canRefundAll, contractID, deviceOrderCompletedPath }) => {
		const readonlyPath = CLUB_DOCUMENTS_ROUTER.QUIT.GET_READONLY_PATH(peopleType, referenceID, contractID);
		let pdfs = [completedPath];
		if (deviceOrderCompletedPath) pdfs.push(deviceOrderCompletedPath);
		if (canRefundAll) {
			navigateToCompletedContract({ pdfPath: pdfs, targetPath: readonlyPath });
		} else {
			setMessageDialog({
				open: true,
				title: "提醒",
				MsgComp: () => (
					<>
						<Typography>若有費用需退回會員，不是在本系統處理的批次扣款與發票開立，請特案處理唷！</Typography>
						<Typography>備註：</Typography>
						<ol>
							<li>金流：請採現金退款或填寫紙本刷退申請書向銀行刷退。</li>
							<li>發票：請至綠界網頁後台執行發票作廢或折讓。</li>
						</ol>
					</>
				),
				onConfirm: () => navigateToCompletedContract({ pdfPath: pdfs, targetPath: readonlyPath }),
			});
		}
	};

	const handleSaveDraftSuccess = () => {
		history.push(returnHistoryPath);
	};

	const formAdapter = {
		getFormData: () => {
			let result = Object.keys(formRef.current).reduce((params, key) => {
				let value = formRef.current[key]?.getResult();
				if (Array.isArray(value)) value = getCheckedBooleanValue(value); // CheckboxField 會回傳陣列d
				return {
					...params,
					[key]: value,
				};
			}, {});

			const trialCalculationResult = trialCalculationRef.current?.getResult();
			const settlementResult = settlementRef.current?.getResult();

			return {
				...result,
				...trialCalculationResult,
				...settlementResult,
			};
		},
		validateForm: () => {
			return !refIsRequiredError(rentCabinetConfirmRef, formRef, settlementRef, trialCalculationRef, deviceConfirmRef);
		},
	};

	const { formData, updateField, validationErrors, onSubmitForm, onSaveDraft, manualTriggerRangeFee } = useMembershipQuitForm({
		formAdapter,
		initFormData: handleInitialFormData(other),
		onSubmitSuccess: onSubmitFormSuccess,
		onSavaDraftSuccess: handleSaveDraftSuccess,
	});

	const handleDateFieldChange = key => (node, value) => {
		// 無效日期不做事
		if (!isValidDate(value)) return false;
		updateField(key, value);
	};

	const handleSubmitForm = () => {
		focusModeRef.current.checkAndExitFocusMode(onSubmitForm);
	};

	const handleSaveDraft = () => {
		focusModeRef.current.checkAndExitFocusMode(onSaveDraft);
	};

	return (
		<MembershipQuitFormProvider
			value={{
				formData,
				validationErrors,
				updateField,
				dialogRef,
				manualTriggerRangeFee,
				isReadOnly,
			}}
		>
			<Card>
				<Card.CardHeader title="填寫表單" className="bg-light text-center" />
				<Card.CardContent className="px-4 py-5">
					<FieldGroup label="表單資訊">
						<Grid item xs={4}>
							<DateField
								label="申請日期"
								ref={el => (formRef.current.applyDate = el)}
								key={parseDate(formData?.applyDate)}
								defaultValue={parseDate(formData?.applyDate)}
								onChange={handleDateFieldChange("applyDate")}
								{...getErrorModuleStatus(validationErrors, "applyDate")}
								readOnly={isReadOnly}
								fullWidth
							/>
						</Grid>
						<Grid item xs={4}>
							<ReadTextField label="會籍終止生效日">{displayDateText(formData?.quitDate)}</ReadTextField>
						</Grid>
						<Grid item xs={4}>
							<EmployeeSelect
								label="經辦教練"
								ref={el => (formRef.current.employeeID = el)}
								key={formData?.employeeID}
								defaultValue={formData?.employeeID}
								onChange={v => updateField("employeeID", v)}
								{...getErrorModuleStatus(validationErrors, "employeeID")}
								options={resource?.coachOptions || []}
								readOnly={isReadOnly}
								fullWidth
								required
							/>
						</Grid>
					</FieldGroup>
					<FieldGroup label="意見回饋">
						<Grid item xs={12}>
							<CheckboxField
								ref={el => (formRef.current.isExtendEndDate = el)}
								key={convertBooleanToOptionArray(formData?.isExtendEndDate)}
								defaultValue={convertBooleanToOptionArray(formData?.isExtendEndDate)}
								onChange={e => updateField("isExtendEndDate", e.target.checked)}
								{...getErrorModuleStatus(validationErrors, "isExtendEndDate")}
								readOnly={isReadOnly}
							>
								<CheckboxField.Checkbox label="已與會員協商可運動到月底，故調整會籍終止生效日" value={1} />
							</CheckboxField>
						</Grid>
						<Grid item xs={8}>
							<SelectField
								label="終止原因"
								ref={el => (formRef.current.reasonType = el)}
								key={formData?.reasonType}
								defaultValue={formData?.reasonType}
								onChange={(e, n, v) => updateField("reasonType", v)}
								{...getErrorModuleStatus(validationErrors, "reasonType")}
								readOnly={isReadOnly}
								displayEmpty
								fullWidth
								required
							>
								{resource?.quitReasonTypeOptions?.map(options => (
									<MenuItem key={options.value} value={options.value}>
										{options.text}
									</MenuItem>
								))}
							</SelectField>
						</Grid>
						<Grid item xs={8}>
							<TextField
								label="誠摯邀請您對於店舖服務，提供寶貴的意見"
								ref={el => (formRef.current.reasonText = el)}
								key={formData?.reasonText}
								defaultValue={formData?.reasonText}
								onChangeNative={e => updateField("reasonText", e.target.value)}
								{...getErrorModuleStatus(validationErrors, "reasonText")}
								readOnly={isReadOnly}
								multiline
								fullWidth
								rows={4}
							/>
						</Grid>
					</FieldGroup>
					{!isReadOnly && (
						<FieldGroup>
							<Grid item xs={12}>
								<Stack direction={"row"} alignItems="start">
									<CheckboxField ref={rentCabinetConfirmRef} readOnly={isReadOnly} required>
										<CheckboxField.Checkbox label="已辦理置物櫃退租作業" value={1} />
									</CheckboxField>
									<Button
										variant="outlined"
										onClick={() =>
											dialogRef.current?.openQuitAllRentCabinetDialog({
												memberID: formData?.memberID,
												membershipID: formData?.membershipID,
											})
										}
									>
										辦理租櫃退租
									</Button>
								</Stack>
							</Grid>
						</FieldGroup>
					)}
				</Card.CardContent>
				<Card.CardHeader title="費用計算" className="bg-light text-center" />
				<Card.CardContent className="px-4 py-5">
					<Grid container spacing={3}>
						<Grid item xs={6}>
							{String(formData?.contractVersion) === "101" && (
								<MembershipQuitTrialCalculation101 ref={trialCalculationRef} />
							)}
							{latestVersions.some(version => isEqual(formData?.contractVersion, version)) ? (
								<MembershipQuitTrialCalculation102 ref={trialCalculationRef} />
							) : null}
						</Grid>
						<Grid item xs={6}>
							{String(formData?.contractVersion) === "101" && (
								<MembershipQuitSettlement101 ref={settlementRef} />
							)}
							{latestVersions.some(version => isEqual(formData?.contractVersion, version)) ? (
								<MembershipQuitSettlement102 ref={settlementRef} />
							) : null}
						</Grid>
					</Grid>
					<Box className="mt-5">
						<FieldGroup label="注意事項">
							<Grid item xs={12}>
								<Alert severity="info" variant="filled" color="info" className="alert-justify-content-start">
									為保障個人權益，完成以上所有條款 / 流程，方視為會員權終止手續之完成，同時我們將在會籍終止日後清除您的付款資訊，若日後需要重新加入需要再重新提供。
								</Alert>
							</Grid>
							<Grid item xs={12}>
								<Alert severity="success" variant="filled" color="success" className="alert-justify-content-start">
									謝謝您這段時間選擇 Curves 陪伴運動，相信您了解持續運動帶給您的好處，期待您會將運動帶進生活， Curves 祝福您。
								</Alert>
							</Grid>

							<Grid item xs={12}>
								{hasDeviceOrder ? (
									<CheckboxField ref={deviceConfirmRef} readOnly={isReadOnly} required>
										<CheckboxField.Checkbox
											label={
												<Box>
													<Typography>本人已瞭解並同意在會籍終止後，同步終止智能系統訂閱，並停止其相關使用權限。</Typography>
													<Typography>1. 會員如於當月15日(含)前提出終止申請，得申請當月份系統使用費之50%(新台幣125元)退款。</Typography>
													<Typography>2. 退款依公司指定線上申請辦理程序。</Typography>
												</Box>
											}
											value={1}
										/>
									</CheckboxField>
								) : null}
								<CheckboxField
									ref={el => (formRef.current.isConfirm = el)}
									key={convertBooleanToOptionArray(formData?.isConfirm)}
									defaultValue={convertBooleanToOptionArray(formData?.isConfirm)}
									onChange={e => updateField("isConfirm", e.target.checked)}
									fullWidth
									{...getErrorModuleStatus(validationErrors, "isConfirm")}
									readOnly={isReadOnly}
									required
								>
									<CheckboxField.Checkbox label="本人已瞭解、詳讀會員權終止條款，並同意遵守。" value={1} />
								</CheckboxField>
							</Grid>
							<Grid item xs={12}>
								<Typography>我們希望持續提供健康與優惠資訊，無論您是否續約，都能獲得實用內容，幫助維持良好生活品質與健康狀態。您同意繼續接收相關通知嗎？</Typography>
								<RadioField
									ref={el => (formRef.current.isAgreeSMS = el)}
									key={convertBooleanToOptionArray(formData?.isAgreeSMS)}
									defaultValue={convertBooleanToOptionArray(formData?.isAgreeSMS)}
									onChange={e => updateField("isConfirm", e.target.value)}
									{...getErrorModuleStatus(validationErrors, "isConfirm")}
									readOnly={isReadOnly}
									required
									row
								>
									<RadioField.Radio label="同意" value={1} />
									<RadioField.Radio label="不同意" value={2} />
								</RadioField>
								<Typography>後續您也可以隨時向我們申請停止接收通知。</Typography>
							</Grid>
							<Grid item xs={6}>
								<SignBox
									title="會員簽名"
									ref={el => (formRef.current.signImg = el)}
									key={formData?.signImg}
									{...getErrorModuleStatus(validationErrors, "signImg")}
									readOnly={isReadOnly}
									required
								/>
							</Grid>
						</FieldGroup>
					</Box>
				</Card.CardContent>
				<Card.CardContent>
					{!isReadOnly ? (
						<Box className="btn-group text-center my-2">
							{!formData?.isCompleteContract && (
								<Button name="temporary" variant="outlined" color="secondary" className="btn-minWidth" onClick={handleSaveDraft}>
									暫存檔案
								</Button>
							)}
							<Button name="create" variant="contained" color="secondary" className="btn-minWidth" onClick={handleSubmitForm}>
								確認送出
							</Button>
						</Box>
					) : (
						<Box className="btn-group text-center my-1">
							<Button variant="contained" color="secondary" className="btn-minWidth" component={Link} to={returnHistoryPath}>
								返回歷史紀錄
							</Button>
						</Box>
					)}
					<Typography variant="body2" className="text-center mb-0 px-2 font-color-3 my-4">
						{formData?.contractVersionText}
					</Typography>
				</Card.CardContent>
			</Card>
		</MembershipQuitFormProvider>
	);
};

export default MembershipQuitForm;
